import { store } from "../store";
import {
  setConnectionStatus,
  addHeartRateData,
  addBreathRateData,
  addObjectDetection,
  addThermalData,
  addImage,
  addDogPose,
  clearHeartRateBuffer,
  clearBreathRateBuffer,
  clearThermalBuffer,
  setSimulating,
  setSessionActive,
  type ObjectDetection,
  type ImageData,
} from "../store/slices/dashboardSlice";
import type { PlaybackData } from "../store/slices/dashboardSlice";

const SERVER_URL = import.meta.env.VITE_WS_URL ||
  (window.location.protocol === 'https:'
    ? `wss://${window.location.host}`
    : `ws://${window.location.host}`);
const API_URL = import.meta.env.VITE_API_URL || window.location.origin;
const EMAIL = import.meta.env.VITE_DASHBOARD_EMAIL || "test2@test.com";
const PASSWORD = import.meta.env.VITE_DASHBOARD_PASSWORD || "test123456";

interface WsMessage {
  type: "request" | "event";
  id?: string;
  method?: string;
  params?: unknown;
  token?: string;
  success?: boolean;
  data?: unknown;
  event?: string;
  payload?: unknown;
  error?: string;
}

interface DashboardEvent {
  sessionId?: string;
  deviceName: string;
  userId?: string;
  timestamp: number;
}

interface HeartRateEvent extends DashboardEvent {
  bpm: number;
  waveValue?: number;
}

interface BreathRateEvent extends DashboardEvent {
  rate: number;
  waveValue?: number;
}

interface DetectionEvent extends DashboardEvent {
  objectClass: string;
  confidence?: number;
  bboxX?: number;
  bboxY?: number;
  bboxWidth?: number;
  bboxHeight?: number;
}

interface ThermalEvent extends DashboardEvent {
  maxTemp?: number;
  minTemp?: number;
  avgTemp?: number;
  gridData?: number[];
}

interface ImageEvent extends DashboardEvent {
  id: string;
  filename: string;
  width?: number;
  height?: number;
  sizeBytes?: number;
}

class DashboardService {
  private ws: WebSocket | null = null;
  private accessToken: string | null = null;
  private userId: string | null = null;
  private currentSessionId: string | null = null;
  private currentDeviceName: string = "dashboard-client";
  private msgId = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private isConnecting = false;
  private pendingRequests: Map<
    string,
    { resolve: (data: unknown) => void; reject: (err: Error) => void }
  > = new Map();

  async init(): Promise<void> {
    console.log("[DashboardService] Initialized with server:", SERVER_URL);
  }

  private nextId(): string {
    return `client_${++this.msgId}`;
  }

  send(method: string, params?: unknown): Promise<unknown> {
    return new Promise((resolve, reject) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        reject(new Error("WebSocket not connected"));
        return;
      }

      const id = this.nextId();
      this.pendingRequests.set(id, { resolve, reject });

      this.ws.send(
        JSON.stringify({
          type: "request",
          id,
          method,
          params,
          token: this.accessToken,
        }),
      );

      setTimeout(() => {
        if (this.pendingRequests.has(id)) {
          this.pendingRequests.delete(id);
          reject(new Error("Request timeout"));
        }
      }, 10000);
    });
  }

  private handleMessage(msg: WsMessage) {
    if (msg.type === "event") {
      console.log("[DashboardService] Event:", msg.event, msg.payload);
      this.handleEvent(msg.event as string, msg.payload as DashboardEvent);
      return;
    }

    if (msg.id && this.pendingRequests.has(msg.id)) {
      const { resolve, reject } = this.pendingRequests.get(msg.id)!;
      this.pendingRequests.delete(msg.id);

      if (msg.success) {
        resolve(msg.data);
      } else {
        reject(new Error((msg as any).error || "Request failed"));
      }
    }
  }

  private handleEvent(event: string, payload: unknown) {
    switch (event) {
      case "connected": {
        const data = payload as { authenticated?: boolean; userId?: string };
        // Only set userId if it's a real user ID (not 'anonymous' or empty)
        if (data.userId && data.userId !== 'anonymous') {
          this.userId = data.userId;
          console.log("[DashboardService] Received userId from server:", data.userId);
        }
        break;
      }
      case "dashboard:heartRate": {
        const data = payload as HeartRateEvent;
        console.log(
          "[DashboardService] Received heartRate event:",
          data.deviceName,
          data.bpm,
          "BPM",
        );
        if (data.sessionId) {
          this.currentSessionId = data.sessionId;
        }
        store.dispatch(
          addHeartRateData({
            deviceName: data.deviceName,
            timestamp: data.timestamp,
            bpm: data.bpm,
            waveValue: data.waveValue ?? 0,
          }),
        );
        break;
      }
      case "dashboard:breathRate": {
        const data = payload as BreathRateEvent;
        console.log(
          "[DashboardService] Received breathRate event:",
          data.deviceName,
          data.rate,
          "/min",
        );
        if (data.sessionId) {
          this.currentSessionId = data.sessionId;
        }
        store.dispatch(
          addBreathRateData({
            deviceName: data.deviceName,
            timestamp: data.timestamp,
            rate: data.rate,
            waveValue: data.waveValue ?? 0,
          }),
        );
        break;
      }
      case "dashboard:detection": {
        const data = payload as DetectionEvent;
        if (data.sessionId) {
          this.currentSessionId = data.sessionId;
        }
        const detection: ObjectDetection = {
          id: `det_${data.timestamp}`,
          timestamp: data.timestamp,
          objectClass: data.objectClass,
          confidence: data.confidence ?? 0,
          bbox: {
            x: data.bboxX ?? 0,
            y: data.bboxY ?? 0,
            width: data.bboxWidth ?? 0,
            height: data.bboxHeight ?? 0,
          },
        };
        store.dispatch(addObjectDetection(detection));
        break;
      }
      case "dashboard:thermal": {
        const data = payload as ThermalEvent;
        if (data.sessionId) {
          this.currentSessionId = data.sessionId;
        }
        store.dispatch(
          addThermalData({
            deviceName: data.deviceName,
            timestamp: data.timestamp,
            maxTemp: data.maxTemp ?? 0,
            minTemp: data.minTemp ?? 0,
            avgTemp: data.avgTemp ?? 0,
            gridData: data.gridData ?? [],
          }),
        );
        break;
      }
      case "dashboard:image": {
        const data = payload as ImageEvent & { image?: string };
        if (data.sessionId) {
          this.currentSessionId = data.sessionId;
        }
        const imageData: ImageData & { image?: string } = {
          id: data.id,
          sessionId: data.sessionId,
          deviceName: data.deviceName,
          timestamp: data.timestamp,
          filename: data.filename,
          width: data.width,
          height: data.height,
          sizeBytes: data.sizeBytes,
          image: data.image, // Include base64 for live viewing without session
        };
        store.dispatch(addImage(imageData));
        break;
      }
      case "dashboard:alert": {
        const data = payload as { type: string; deviceName: string; message: string; bpm?: number; avgBpm?: number; timestamp: number };
        console.log("[DashboardService] Alert:", data.type, data.message);
        if (data.type === 'agentsDispatched') {
          window.dispatchEvent(new CustomEvent('app:showToast', { detail: { message: `📋 ${data.message}`, type: 'success' } }));
        } else {
          window.dispatchEvent(new CustomEvent('app:showToast', { detail: { message: `⚠️ ${data.message}`, type: 'error' } }));
        }
        break;
      }
      case "dashboard:dogPose": {
        const data = payload as any;
        if (data) {
          store.dispatch(addDogPose(data));
        }
        break;
      }
    }
  }

  async connect(): Promise<boolean> {
    if (this.ws || this.isConnecting) {
      console.log("[DashboardService] Already connected or connecting");
      return true;
    }

    this.isConnecting = true;
    console.log("[DashboardService] Connecting to", SERVER_URL);

    return new Promise((resolve, reject) => {
      this.ws = new WebSocket(SERVER_URL);

      this.ws.onopen = async () => {
        console.log("[DashboardService] WebSocket connected");
        this.isConnecting = false;

        try {
          await this.authenticate();
          // Don't auto-create session - user will control manually
          store.dispatch(
            setConnectionStatus({
              isConnected: true,
              cameraName: "Cloud Server",
              cameraUrl: SERVER_URL,
            }),
          );

          resolve(true);
        } catch (err) {
          console.error("[DashboardService] Connection failed:", err);
          this.disconnect();
          reject(err);
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data) as WsMessage;
          this.handleMessage(msg);
        } catch (err) {
          console.error("[DashboardService] Failed to parse message:", err);
        }
      };

      this.ws.onerror = (err) => {
        console.error("[DashboardService] WebSocket error:", err);
      };

      this.ws.onclose = () => {
        console.log("[DashboardService] WebSocket closed");
        this.ws = null;
        this.accessToken = null;
        this.userId = null;
        this.currentSessionId = null;
        store.dispatch(setConnectionStatus({ isConnected: false }));

        if (!this.reconnectTimeout) {
          this.reconnectTimeout = setTimeout(() => {
            this.reconnectTimeout = null;
            this.connect().catch(console.error);
          }, 5000);
        }
      };
    });
  }

  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.accessToken = null;
    this.userId = null;
    this.currentSessionId = null;
    store.dispatch(setConnectionStatus({ isConnected: false }));
  }

  private async authenticate(): Promise<void> {
    console.log("[DashboardService] Authenticating...");

    // Use existing auth token from the main app login if available
    const existingToken = localStorage.getItem("auth_token");
    if (existingToken) {
      this.accessToken = existingToken;
      await this.ensureSocketAuthenticated();
      console.log("[DashboardService] Authenticated with existing token");
      return;
    }

    // Fallback: auto-register + login with test credentials (dev only)
    console.log("[DashboardService] No existing token, trying test account...");

    try {
      const result = (await this.send("auth:register", {
        email: EMAIL,
        password: PASSWORD,
        role: "user",
      })) as any;

      if (!result.success) {
        if (result.error !== "Email already registered") {
          throw new Error(result.error);
        }
      }
    } catch (err) {
      console.log("[DashboardService] Register failed, trying login...");
    }

    const loginResult = (await this.send("auth:login", {
      email: EMAIL,
      password: PASSWORD,
    })) as any;

    if (!loginResult.success) {
      throw new Error(loginResult.error || "Login failed");
    }

    this.accessToken = String(loginResult.token);
    this.userId = loginResult.userId || null;
    try {
      localStorage.setItem("auth_token", this.accessToken);
    } catch {
      // Ignore storage errors in restricted environments.
    }
    await this.ensureSocketAuthenticated();
    console.log("[DashboardService] Authenticated");
  }

  private async ensureSocketAuthenticated(): Promise<void> {
    if (!this.accessToken) return;

    try {
      // Send one protected request with token so server marks this socket as authenticated.
      await this.send("dashboard:session:list");
      console.log(
        "[DashboardService] WebSocket authenticated for realtime events",
      );
    } catch (err) {
      console.warn(
        "[DashboardService] Failed to confirm WebSocket auth state:",
        err,
      );
    }
  }

  async startSession(subjectName?: string): Promise<boolean> {
    try {
      const result = (await this.send("dashboard:session:start", {
        subjectName,
        deviceName: this.currentDeviceName,
      })) as any;

      if (result?.success) {
        if (result.session?.id) {
          this.currentSessionId = result.session.id;
        }
        store.dispatch(setSessionActive(true));
        store.dispatch(setSimulating(true));
        return true;
      }
      return false;
    } catch (err) {
      console.error("[DashboardService] Failed to start session:", err);
      return false;
    }
  }

  async stopSession(): Promise<void> {
    try {
      if (this.currentSessionId) {
        await this.send("dashboard:session:stop", this.currentSessionId);
      }
      store.dispatch(setSessionActive(false));
      store.dispatch(setSimulating(false));
    } catch (err) {
      console.error("[DashboardService] Failed to stop session:", err);
    }
  }

  async queryRecentData(limit = 100): Promise<void> {
    // Query historical data from server (optional - mainly for reconnecting or reviewing past sessions)
    // Live data comes through WebSocket events, not this function
    if (!this.currentSessionId) {
      console.log(
        "[DashboardService] queryRecentData: no session, skipping historical data load",
      );
      return;
    }

    store.dispatch(clearHeartRateBuffer());
    store.dispatch(clearBreathRateBuffer());
    store.dispatch(clearThermalBuffer());

    try {
      const [hrResult, brResult, thermalResult] = await Promise.all([
        this.send("dashboard:heartRate:query", {
          sessionId: this.currentSessionId,
          limit,
        }) as Promise<any>,
        this.send("dashboard:breathRate:query", {
          sessionId: this.currentSessionId,
          limit,
        }) as Promise<any>,
        this.send("dashboard:thermal:query", {
          sessionId: this.currentSessionId,
          limit: 10,
        }) as Promise<any>,
      ]);

      if (hrResult.success && hrResult.data) {
        for (const point of hrResult.data) {
          store.dispatch(
            addHeartRateData({
              deviceName: point.deviceName || "unknown",
              timestamp: point.timestamp,
              bpm: point.bpm,
              waveValue: point.waveValue,
            }),
          );
        }
      }

      if (brResult.success && brResult.data) {
        for (const point of brResult.data) {
          store.dispatch(
            addBreathRateData({
              deviceName: point.deviceName || "unknown",
              timestamp: point.timestamp,
              rate: point.rate,
              waveValue: point.waveValue,
            }),
          );
        }
      }

      if (thermalResult.success && thermalResult.data) {
        for (const point of thermalResult.data) {
          store.dispatch(
            addThermalData({
              deviceName: point.deviceName || "unknown",
              timestamp: point.timestamp,
              maxTemp: point.maxTemp,
              minTemp: point.minTemp,
              avgTemp: point.avgTemp,
              gridData: point.gridData,
            }),
          );
        }
      }
    } catch (err) {
      console.error("[DashboardService] Failed to query recent data:", err);
    }
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  getCurrentDeviceName(): string {
    return this.currentDeviceName;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  async insertObjectDetection(detection: {
    objectClass: string;
    confidence?: number;
    bboxX?: number;
    bboxY?: number;
    bboxWidth?: number;
    bboxHeight?: number;
    metadata?: Record<string, unknown>;
  }): Promise<boolean> {
    try {
      const result = (await this.send("dashboard:detections.insert", {
        deviceName: this.currentDeviceName,
        sessionId: this.currentSessionId || undefined,
        timestamp: Date.now(),
        objectClass: detection.objectClass,
        confidence: detection.confidence,
        bboxX: detection.bboxX,
        bboxY: detection.bboxY,
        bboxWidth: detection.bboxWidth,
        bboxHeight: detection.bboxHeight,
        metadata: detection.metadata,
      })) as any;

      if (!result.success) {
        console.error(
          "[DashboardService] Failed to insert detection:",
          result.error,
        );
        return false;
      }

      return true;
    } catch (err) {
      console.error("[DashboardService] Failed to insert detection:", err);
      return false;
    }
  }

  async insertDogPose(pose: {
    imageId: string;
    startedAt?: number;
    dogState?: string;
    bboxX: number;
    bboxY: number;
    bboxWidth: number;
    bboxHeight: number;
    bboxConfidence: number;
    headCenterX?: number;
    headCenterY?: number;
    tailBaseX?: number;
    tailBaseY?: number;
    facingDirection: string;
    facingAngle: number;
    facingAngleDegrees?: number;
    facingConfidence: number;
    keypoints: Array<{
      name: string;
      x: number;
      y: number;
      confidence: number;
    }>;
    isValid?: boolean;
    timestamp?: number;
  }): Promise<boolean> {
    try {
      const params = {
        deviceName: this.currentDeviceName,
        sessionId: this.currentSessionId || undefined,
        userId: this.userId || undefined,
        imageId: pose.imageId,
        timestamp: pose.timestamp ?? Date.now(),
        startedAt: pose.startedAt,
        dogState: pose.dogState,
        bboxX: pose.bboxX,
        bboxY: pose.bboxY,
        bboxWidth: pose.bboxWidth,
        bboxHeight: pose.bboxHeight,
        bboxConfidence: pose.bboxConfidence,
        headCenterX: pose.headCenterX,
        headCenterY: pose.headCenterY,
        tailBaseX: pose.tailBaseX,
        tailBaseY: pose.tailBaseY,
        facingDirection: pose.facingDirection,
        facingAngle: pose.facingAngle,
        facingAngleDegrees: pose.facingAngleDegrees,
        facingConfidence: pose.facingConfidence,
        keypoints: pose.keypoints,
        isValid: pose.isValid ?? true,
      };
      console.log(`[DashboardService] insertDogPose called: sessionId=${this.currentSessionId ?? 'null'} dogState=${pose.dogState ?? 'undefined'}`);
      const result = (await this.send("dashboard:dogPose:insert", params)) as any;

      if (!result.success) {
        console.error(
          "[DashboardService] Failed to insert dog pose:",
          result.error,
        );
        return false;
      }

      return true;
    } catch (err) {
      console.error("[DashboardService] Failed to insert dog pose:", err);
      return false;
    }
  }

  async getDogPoses(sessionId: string, from?: number, to?: number, limit?: number): Promise<any[]> {
    try {
      const params: Record<string, unknown> = { sessionId };
      if (from !== undefined) params.from = from;
      if (to !== undefined) params.to = to;
      if (limit !== undefined) params.limit = limit;
      const result = (await this.send("dashboard:dogPose:query", params)) as any;
      if (!result.success) {
        console.error("[DashboardService] Failed to query dog poses:", result.error);
        return [];
      }
      return result.poses ?? [];
    } catch (err) {
      console.error("[DashboardService] Failed to query dog poses:", err);
      return [];
    }
  }

  /** Export dog poses for a session as a CSV file download */
  /** Shared headers for dog pose export */
  private dogPoseExportHeaders(): string[] {
    return [
      'id', 'session_id', 'timestamp', 'started_at', 'dog_state',
      'bbox_x', 'bbox_y', 'bbox_width', 'bbox_height', 'bbox_confidence',
      'head_center_x', 'head_center_y', 'tail_base_x', 'tail_base_y',
      'facing_direction', 'facing_angle', 'facing_angle_degrees', 'facing_confidence',
      'keypoints', 'is_valid', 'created_at'
    ];
  }

  /** Map a pose object to a row array (raw values, no CSV escaping) */
  private dogPoseToRow(pose: any, sessionId: string): unknown[] {
    return [
      pose.id ?? '',
      pose.sessionId ?? pose.session_id ?? sessionId,
      pose.timestamp ?? '',
      pose.startedAt ?? pose.started_at ?? '',
      pose.dogState ?? pose.dog_state ?? '',
      pose.bboxX ?? pose.bbox_x ?? '',
      pose.bboxY ?? pose.bbox_y ?? '',
      pose.bboxWidth ?? pose.bbox_width ?? '',
      pose.bboxHeight ?? pose.bbox_height ?? '',
      pose.bboxConfidence ?? pose.bbox_confidence ?? '',
      pose.headCenterX ?? pose.head_center_x ?? '',
      pose.headCenterY ?? pose.head_center_y ?? '',
      pose.tailBaseX ?? pose.tail_base_x ?? '',
      pose.tailBaseY ?? pose.tail_base_y ?? '',
      pose.facingDirection ?? pose.facing_direction ?? '',
      pose.facingAngle ?? pose.facing_angle ?? '',
      pose.facingAngleDegrees ?? pose.facing_angle_degrees ?? '',
      pose.facingConfidence ?? pose.facing_confidence ?? '',
      pose.keypoints ? JSON.stringify(pose.keypoints) : '[]',
      pose.isValid ?? pose.is_valid ?? '',
      pose.createdAt ?? pose.created_at ?? '',
    ];
  }

  async exportDogPosesCsv(sessionId: string, from?: number, to?: number): Promise<{ success: boolean; count: number }> {
    const poses = await this.getDogPoses(sessionId, from, to, undefined);
    if (poses.length === 0) {
      console.warn("[DashboardService] No poses to export for session:", sessionId);
      return { success: false, count: 0 };
    }

    const headers = this.dogPoseExportHeaders();
    const rows = poses.map((pose: any) =>
      this.dogPoseToRow(pose, sessionId).map(val => {
        const str = String(val);
        // Escape fields that contain commas, quotes, or newlines
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      })
    );

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');

    // Trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dog_poses_${sessionId}_${new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return { success: true, count: poses.length };
  }

  async exportDogPosesXlsx(sessionId: string, from?: number, to?: number): Promise<{ success: boolean; count: number }> {
    const XLSX = await import('@e965/xlsx');
    const poses = await this.getDogPoses(sessionId, from, to, undefined);
    if (poses.length === 0) {
      console.warn("[DashboardService] No poses to export for session:", sessionId);
      return { success: false, count: 0 };
    }

    const headers = this.dogPoseExportHeaders();
    const rows = poses.map((pose: any) => this.dogPoseToRow(pose, sessionId));
    const aoa = [headers, ...rows];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    XLSX.utils.book_append_sheet(wb, ws, 'Dog Poses');
    XLSX.writeFileXLSX(wb, `dog_poses_${sessionId}_${new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')}.xlsx`);
    return { success: true, count: poses.length };
  }

  async getPosesFrequency(sessionId: string, from: number, to: number): Promise<Record<string, number>> {
    try {
      const result = (await this.send("dashboard:dogPose:frequency", { sessionId, from, to })) as any;
      if (!result.success) {
        console.error("[DashboardService] Failed to get poses frequency:", result.error);
        return {};
      }
      return result.frequency ?? {};
    } catch (err) {
      console.error("[DashboardService] Failed to get poses frequency:", err);
      return {};
    }
  }

  async getPosesDuration(sessionId: string, from: number, to: number): Promise<Record<string, number>> {
    try {
      const result = (await this.send("dashboard:dogPose:duration", { sessionId, from, to })) as any;
      if (!result.success) {
        console.error("[DashboardService] Failed to get poses duration:", result.error);
        return {};
      }
      return result.duration ?? {};
    } catch (err) {
      console.error("[DashboardService] Failed to get poses duration:", err);
      return {};
    }
  }

  async getDistinctDogStates(sessionId: string): Promise<string[]> {
    try {
      const result = (await this.send("dashboard:dogPose:states", { sessionId })) as any;
      if (!result.success) {
        console.error("[DashboardService] Failed to get distinct dog states:", result.error);
        return [];
      }
      return result.states ?? [];
    } catch (err) {
      console.error("[DashboardService] Failed to get distinct dog states:", err);
      return [];
    }
  }

  async listSessions(): Promise<Array<{
    id: string;
    status: string;
    startedAt: number | null;
    endedAt: number | null;
  }>> {
    try {
      const result = (await this.send("dashboard:session:list")) as any;
      if (result?.success && result.sessions) {
        return result.sessions;
      }
      return [];
    } catch (err) {
      console.error("[DashboardService] Failed to list sessions:", err);
      return [];
    }
  }

  async fetchPlayback(deviceName: string, issueType?: string, from?: string, to?: string): Promise<PlaybackData | null> {
    try {
      const params = new URLSearchParams({ deviceName });
      if (issueType) params.set('issueType', issueType);
      if (from) params.set('from', from);
      if (to) params.set('to', to);

      const token = localStorage.getItem('auth_token');
      const url = `${API_URL}/api/dashboard/playback?${params.toString()}`;
      const response = await fetch(url, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        console.error('[DashboardService] Playback fetch failed:', response.status, response.statusText);
        return null;
      }

      const data = await response.json();
      if (!data.success) {
        console.error('[DashboardService] Playback API error:', data.error);
        return null;
      }

      return {
        deviceName: data.deviceName,
        issueType: data.issueType,
        from: data.from,
        to: data.to,
        heartRate: data.heartRate || [],
        breathRate: data.breathRate || [],
        thermal: data.thermal || [],
        dogPoses: data.dogPoses || [],
        images: data.images || [],
        eventFolders: data.eventFolders || [],
      };
    } catch (err) {
      console.error('[DashboardService] Failed to fetch playback:', err);
      return null;
    }
  }

  async fetchDevices(): Promise<string[]> {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/dashboard/devices`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        console.error('[DashboardService] Devices fetch failed:', response.status, response.statusText);
        return [];
      }

      const data = await response.json();
      if (!data.success) {
        console.error('[DashboardService] Devices API error:', data.error);
        return [];
      }

      return data.devices || [];
    } catch (err) {
      console.error('[DashboardService] Failed to fetch devices:', err);
      return [];
    }
  }
}

export const dashboardService = new DashboardService();
